/**
 * Blazorly Builder Builder App
 *
 * Main application entry point with routing for home and builder pages.
 * Uses React.lazy and Suspense for code splitting to reduce initial bundle size.
 */

import { BrowserRouter, Routes, Route, Navigate, useParams, useSearchParams, useLocation } from 'react-router';
import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { ToastProvider } from './contexts/ToastContext';
import { ToastContainer } from './components/common/ToastContainer';
import { touchProject } from './lib/projectStorage';
import { openAgentStudio, config } from './config';
import { getStoredToken } from './lib/authClient';
import { builderReturnPath, builderReturnUrl, identityHandoffUrl, inviteIdFromSearch } from './lib/authRedirect';

// Lazy load page components for code splitting
const HomePage = lazy(() => import('./pages/Home').then(m => ({ default: m.HomePage })));
const BuilderPage = lazy(() => import('./pages/Builder').then(m => ({ default: m.BuilderPage })));
const ProjectListPage = lazy(() => import('./pages/projects/ProjectList').then(m => ({ default: m.ProjectListPage })));
const ProjectDetail = lazy(() => import('./pages/projects/ProjectDetail').then(m => ({ default: m.ProjectDetail })));
const PlanningBoard = lazy(() => import('./pages/projects/PlanningBoard').then(m => ({ default: m.PlanningBoard })));
const ProjectSchemaEditor = lazy(() => import('./pages/projects/ProjectSchemaEditor').then(m => ({ default: m.ProjectSchemaEditor })));
const ProjectStudio = lazy(() => import('./pages/projects/ProjectStudio').then(m => ({ default: m.ProjectStudio })));
const ProjectSettings = lazy(() => import('./pages/projects/ProjectSettings').then(m => ({ default: m.ProjectSettings })));
const Callback = lazy(() => import('./pages/auth/Callback').then(m => ({ default: m.Callback })));
const VerifyEmail = lazy(() => import('./pages/auth/VerifyEmail').then(m => ({ default: m.VerifyEmail })));

/**
 * Redirect an auth path to the platform identity service (auth.blazorly.com).
 * Login/signup/password-recovery UI lives there; builder keeps only the
 * backend-driven OAuth callback and email verification.
 *
 * The `?redirect=` target is never this page (`/login` etc.) — that used to
 * bounce the browser back here after sign-in and loop with the identity
 * service. Already-authed visitors skip the hop and land on the return path.
 */
function RedirectToAuth({ path }: { path: string }) {
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const returnTo = builderReturnPath(location);
  const returnUrl = builderReturnUrl(location);
  const inviteId = inviteIdFromSearch(location.search);

  useEffect(() => {
    if (isLoading || isAuthenticated) return;
    window.location.href = identityHandoffUrl(config.authAppUrl, path, returnUrl, inviteId);
  }, [path, returnUrl, inviteId, isLoading, isAuthenticated]);

  if (isLoading) {
    return <PageLoader />;
  }

  if (isAuthenticated) {
    return <Navigate to={returnTo} replace />;
  }

  return <PageLoader />;
}

const OrganizationList = lazy(() => import('./pages/organizations').then(m => ({ default: m.OrganizationList })));
const OrganizationSettings = lazy(() => import('./pages/organizations').then(m => ({ default: m.OrganizationSettings })));
const TeamSettings = lazy(() => import('./pages/teams').then(m => ({ default: m.TeamSettings })));
const ProfileSettings = lazy(() => import('./pages/settings').then(m => ({ default: m.ProfileSettings })));
const AccountSettings = lazy(() => import('./pages/settings').then(m => ({ default: m.AccountSettings })));
const PreferencesSettings = lazy(() => import('./pages/settings').then(m => ({ default: m.PreferencesSettings })));
const APIKeysSettings = lazy(() => import('./pages/settings').then(m => ({ default: m.APIKeysSettings })));
const AgentMemorySettings = lazy(() => import('./pages/settings').then(m => ({ default: m.AgentMemorySettings })));
const BillingDashboard = lazy(() => import('./pages/billing/BillingDashboard').then(m => ({ default: m.BillingDashboard })));
const DatabaseList = lazy(() => import('./pages/databases/DatabaseList').then(m => ({ default: m.DatabaseList })));
const ProjectDeployments = lazy(() => import('./pages/projects/ProjectDeployments').then(m => ({ default: m.ProjectDeployments })));
const UsageDashboard = lazy(() => import('./pages/usage/UsageDashboard').then(m => ({ default: m.UsageDashboard })));
const InsightsPage = lazy(() => import('./pages/insights/InsightsPage').then(m => ({ default: m.InsightsPage })));
const TemplatesPage = lazy(() => import('./pages/templates/TemplatesPage').then(m => ({ default: m.TemplatesPage })));
/**
 * Agents live in Blazorly Agent Studio (separate app).
 * Deep links under /agents/* open Studio and bounce home in Builder.
 */
function AgentStudioRedirect() {
  const { id } = useParams();
  useEffect(() => {
    openAgentStudio(id ? `/agents/${encodeURIComponent(id)}` : '/agents');
  }, [id]);
  return <Navigate to="/" replace />;
}

/**
 * Project route that extracts projectId and templateId from URL and renders the builder
 */
function ProjectRoute() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('template') || undefined;

  useEffect(() => {
    if (id) {
      // Update last accessed time when opening a project
      touchProject(id);
    }
  }, [id]);

  if (!id) {
    return <Navigate to="/" replace />;
  }

  return <BuilderPage projectId={id} templateId={templateId} />;
}

/**
 * Redirects from legacy ?projectId= query param to new /project/:id route
 */
function LegacyBuilderRedirect() {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');

  if (projectId) {
    return <Navigate to={`/project/${projectId}`} replace />;
  }

  return <Navigate to="/" replace />;
}

/**
 * Suspense wrapper for lazy-loaded routes
 */
function SuspenseWrapper({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<PageLoader />}>
      {children}
    </Suspense>
  );
}

/**
 * Warms the builder chunk while auth resolves. /project/:id otherwise pays
 * for auth (/me) and THEN the ~300KB chunk download serially; a stored token
 * means the user is almost certainly authenticated, so start the download
 * immediately. React.lazy dedups against the warmed module cache.
 */
function RoutePrefetcher() {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname.startsWith('/project/') && getStoredToken()) {
      void import('./pages/Builder');
    }
  }, [location.pathname]);
  return null;
}

/**
 * Loading fallback component for Suspense boundaries
 * Displays a centered spinner while lazy-loaded components are loading
 */
function PageLoader() {
  return (
    <div className="app-canvas flex min-h-screen items-center justify-center px-4">
      <div className="surface-panel-strong flex flex-col items-center rounded-card px-8 py-8 text-center">
        <div className="h-12 w-12 animate-spin rounded-full border-[3px] border-primary/20 border-t-primary" />
        <p className="mt-4 text-sm font-medium text-foreground">Loading workspace...</p>
        <p className="mt-1 text-xs text-muted-foreground">Preparing the next surface.</p>
      </div>
    </div>
  );
}

/**
 * Protected route wrapper that requires authentication
 * Redirects to login if not authenticated
 */
function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    // Redirect to login, saving the intended destination
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <BrowserRouter>
      <RoutePrefetcher />
      <ThemeProvider>
      <AuthProvider>
        <OrganizationProvider>
        <ToastProvider>
          <ToastContainer />
          <SuspenseWrapper>
          <Routes>
            {/* Auth routes redirect to the platform identity service */}
            <Route path="/login" element={<RedirectToAuth path="/login" />} />
            <Route path="/signup" element={<RedirectToAuth path="/register" />} />
            <Route path="/forgot-password" element={<RedirectToAuth path="/forgot-password" />} />
            <Route path="/reset-password" element={<RedirectToAuth path="/reset-password" />} />
            {/* Backend-driven flows that remain on the builder origin */}
            <Route path="/auth/callback" element={<Callback />} />
            <Route path="/auth/verify-email" element={<VerifyEmail />} />

            {/* Protected routes - require authentication */}
            <Route path="/" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
            <Route path="/projects" element={<ProtectedRoute><ProjectListPage /></ProtectedRoute>} />
            <Route path="/project/:id" element={<ProtectedRoute><ProjectRoute /></ProtectedRoute>} />
            <Route path="/project/:id/detail" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
            <Route path="/project/:id/board" element={<ProtectedRoute><PlanningBoard /></ProtectedRoute>} />
            <Route path="/project/:id/settings" element={<ProtectedRoute><ProjectSettings /></ProtectedRoute>} />
            <Route path="/project/:id/schema" element={<ProtectedRoute><ProjectSchemaEditor /></ProtectedRoute>} />
            <Route path="/project/:id/studio" element={<ProtectedRoute><ProjectStudio /></ProtectedRoute>} />
            <Route path="/project/:id/studio/:module" element={<ProtectedRoute><ProjectStudio /></ProtectedRoute>} />
            <Route path="/project/:id/studio/:module/:tab" element={<ProtectedRoute><ProjectStudio /></ProtectedRoute>} />
            <Route path="/project/:id/deployments" element={<ProtectedRoute><ProjectDeployments /></ProtectedRoute>} />
            {/* Organization routes */}
            <Route path="/organizations" element={<ProtectedRoute><OrganizationList /></ProtectedRoute>} />
            <Route path="/organizations/:id" element={<ProtectedRoute><OrganizationSettings /></ProtectedRoute>} />
            {/* Team routes */}
            <Route path="/teams/:id" element={<ProtectedRoute><TeamSettings /></ProtectedRoute>} />
            {/* Settings routes */}
            <Route path="/settings/profile" element={<ProtectedRoute><ProfileSettings /></ProtectedRoute>} />
            <Route path="/settings/account" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
            <Route path="/settings/preferences" element={<ProtectedRoute><PreferencesSettings /></ProtectedRoute>} />
            <Route path="/settings/api-keys" element={<ProtectedRoute><APIKeysSettings /></ProtectedRoute>} />
            <Route path="/settings/agent-memory" element={<ProtectedRoute><AgentMemorySettings /></ProtectedRoute>} />
            {/* Redirect /settings to /settings/profile */}
            <Route path="/settings" element={<ProtectedRoute><Navigate to="/settings/profile" replace /></ProtectedRoute>} />
            {/* Billing routes */}
            <Route path="/billing" element={<ProtectedRoute><BillingDashboard /></ProtectedRoute>} />
            {/* Usage & Analytics */}
            <Route path="/usage" element={<ProtectedRoute><UsageDashboard /></ProtectedRoute>} />
            <Route path="/insights" element={<ProtectedRoute><InsightsPage /></ProtectedRoute>} />
            {/* Templates gallery */}
            <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
            {/* Agents → external Blazorly Agent Studio */}
            <Route path="/agents" element={<ProtectedRoute><AgentStudioRedirect /></ProtectedRoute>} />
            <Route path="/agents/:id" element={<ProtectedRoute><AgentStudioRedirect /></ProtectedRoute>} />
            {/* Workspace data explorer — rendered inline on /databases; the
                legacy id-bearing admin routes redirect there. */}
            <Route path="/databases" element={<ProtectedRoute><DatabaseList /></ProtectedRoute>} />
            <Route path="/databases/:instanceId/admin" element={<ProtectedRoute><Navigate to="/databases" replace /></ProtectedRoute>} />
            <Route path="/databases/:instanceId/:databaseName/admin" element={<ProtectedRoute><Navigate to="/databases" replace /></ProtectedRoute>} />
            {/* Legacy support for ?projectId= query param */}
            <Route path="/builder" element={<ProtectedRoute><LegacyBuilderRedirect /></ProtectedRoute>} />
            {/* Catch-all redirect to login */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
          </SuspenseWrapper>
        </ToastProvider>
        </OrganizationProvider>
      </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
